import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const when = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

/** One enquiry conversation. `side` is who is looking: 'buyer' | 'seller'. */
export function EnquiryThread({ thread, side, onUpdate }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (thread?.unread) {
      api.post(`/enquiries/${thread.id}/read`).catch(() => {});
      onUpdate?.({ ...thread, unread: false });
    }
  }, [thread?.id, thread?.unread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enquiries/${thread.id}/reply`, { message: text });
      setText("");
      onUpdate?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`enquiry-thread-${thread.id}`} className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="flex items-center justify-between text-xs text-neutral-400">
        <span className="truncate">{side === "seller" ? <>{thread.buyer_name || "Buyer"} · <a href={`mailto:${thread.buyer_email}`} className="hover:underline">{thread.buyer_email}</a></> : <>With {thread.seller_name || "the seller"}</>}</span>
        <span className="truncate text-neutral-500 ml-2">{thread.product_title}</span>
      </div>
      <ul ref={listRef} className="mt-2 space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="enquiry-messages">
        {thread.messages.map((m, i) => {
          const mine = m.from === side;
          return (
            <li key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm ${mine ? "bg-[#FF3B30] text-white rounded-br-sm" : "bg-white/10 text-neutral-100 rounded-bl-sm"}`}>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-neutral-500"}`}>{m.from === "buyer" ? (thread.buyer_name || "Buyer") : (thread.seller_name || "Seller")} · {when(m.at)}{m.via === "email" ? " · via email" : ""}</div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex items-end gap-2">
        <Textarea data-testid="enquiry-reply-input" value={text} onChange={(e) => setText(e.target.value)} rows={1} maxLength={1000}
          placeholder={side === "seller" ? "Reply to the buyer…" : "Reply to the seller…"} className="min-h-[40px] bg-black/40 border-[#27272A] text-sm resize-none" />
        <Button data-testid="enquiry-reply-send" size="sm" onClick={send} disabled={busy || !text.trim()} className="h-10 rounded-full bg-white text-black hover:bg-neutral-200 font-bold px-4">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** All my threads (buyer or seller), newest first. */
export function EnquiriesPanel({ side, compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/enquiries/mine").then(({ data }) => setData(data)).catch(() => setData({ threads: [], unread: 0 }));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const threads = data.threads;
  const patch = (t) => setData((d) => {
    const threads = d.threads.map((x) => (x.id === t.id ? { ...t, unread: false } : x));
    return { ...d, threads, unread: threads.filter((x) => x.unread).length };
  });

  return (
    <div data-testid="enquiries-panel">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-neutral-500">
        <span>Enquiries {data.unread > 0 && <span data-testid="enquiries-unread" className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px]">{data.unread} new</span>}</span>
      </div>
      {threads.length === 0 ? (
        <p className="text-xs text-neutral-500 mt-2" data-testid="enquiries-empty">{side === "seller" ? "No enquiries yet. Buyers who can't check out can message you here." : "You haven't asked a seller anything yet."}</p>
      ) : (
        <ul className={`mt-2 space-y-2 ${compact && open === null ? "max-h-72 overflow-y-auto pr-1" : ""}`}>
          {threads.map((t) => {
            const last = t.messages[t.messages.length - 1];
            return (
              <li key={t.id}>
                {open === t.id ? (
                  <EnquiryThread thread={t} side={side} onUpdate={patch} />
                ) : (
                  <button data-testid={`enquiry-open-${t.id}`} onClick={() => setOpen(t.id)} className="w-full text-left rounded-lg border border-white/10 bg-black/30 px-3 py-2 hover:border-white/30 transition-colors">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-neutral-300">{side === "seller" ? (t.buyer_name || "Buyer") : (t.seller_name || "Seller")} · <span className="text-neutral-500">{t.product_title}</span></span>
                      <span className="flex items-center gap-2 shrink-0 text-neutral-500">{t.unread && <span className="h-2 w-2 rounded-full bg-[#FF3B30]" />}{when(t.last_message_at)}</span>
                    </div>
                    <div className="text-xs text-neutral-200 line-clamp-1 mt-0.5"><span className="text-neutral-500">{last.from === side ? "You: " : ""}</span>{last.text}</div>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
